/**
 * SyntaxHighlight
 * .js-highlightで囲んだエリアのHTMLコードを自動的にエスケープしてPrism.jsでシンタックスハイライト表示
 * 
 * @example
 * HTML:
 * <div class="js-highlight" data-language="html">
 *   <div class="example">
 *     <p>サンプルHTML</p>
 *   </div>
 * </div>
 * 
 * JS: new SyntaxHighlight();
 */
export default class SyntaxHighlight {
  constructor(options = {}) {
    this.options = {
      selector: '.js-highlight',
      className: 'syntax-highlight',
      showLineNumbers: true,
      ...options
    };

    this.loadPrism().then(() => {
      this.init();
    });
  }

  async loadPrism() {
    // Prism.jsが既に読み込まれているかチェック
    if (window.Prism) {
      return Promise.resolve();
    }

    // Prism.js CSS
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism.min.css';
    document.head.appendChild(cssLink);

    // Prism.js Core
    const coreScript = document.createElement('script');
    coreScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-core.min.js';
    
    // Prism.js Auto-loader（自動的に必要な言語ファイルを読み込む）
    const autoloaderScript = document.createElement('script');
    autoloaderScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/autoloader/prism-autoloader.min.js';

    // Line Numbers Plugin
    const lineNumbersCSS = document.createElement('link');
    lineNumbersCSS.rel = 'stylesheet';
    lineNumbersCSS.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/line-numbers/prism-line-numbers.min.css';
    document.head.appendChild(lineNumbersCSS);

    const lineNumbersScript = document.createElement('script');
    lineNumbersScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/line-numbers/prism-line-numbers.min.js';

    // Copy to Clipboard Plugin
    const copyToClipboardCSS = document.createElement('link');
    copyToClipboardCSS.rel = 'stylesheet';
    copyToClipboardCSS.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/copy-to-clipboard/prism-copy-to-clipboard.min.css';
    document.head.appendChild(copyToClipboardCSS);

    const copyToClipboardScript = document.createElement('script');
    copyToClipboardScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/copy-to-clipboard/prism-copy-to-clipboard.min.js';

    // Toolbar Plugin (Copy to Clipboardの前提)
    const toolbarCSS = document.createElement('link');
    toolbarCSS.rel = 'stylesheet';
    toolbarCSS.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/toolbar/prism-toolbar.min.css';
    document.head.appendChild(toolbarCSS);

    const toolbarScript = document.createElement('script');
    toolbarScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/toolbar/prism-toolbar.min.js';

    return new Promise((resolve) => {
      coreScript.onload = () => {
        autoloaderScript.onload = () => {
          toolbarScript.onload = () => {
            lineNumbersScript.onload = () => {
              copyToClipboardScript.onload = () => {
                resolve();
              };
              document.head.appendChild(copyToClipboardScript);
            };
            document.head.appendChild(lineNumbersScript);
          };
          document.head.appendChild(toolbarScript);
        };
        document.head.appendChild(autoloaderScript);
      };
      document.head.appendChild(coreScript);
    });
  }

  init() {
    const elements = document.querySelectorAll(this.options.selector);
    if (elements.length === 0) return;

    elements.forEach(element => {
      this.processElement(element);
    });
  }

  processElement(element) {
    // 既に処理済みの場合はスキップ
    if (element.classList.contains('processed')) return;

    // 元のHTMLコンテンツを取得（無効化処理前に保存）
    const originalHTML = element.innerHTML.trim();

    // 内部のjs-*クラスを無効化（data属性に変更）
    this.disableJsClasses(element);

    // 動的に生成された要素（ulなど）を除去
    this.removeDynamicElements(element);
    
    if (!originalHTML) {
      element.style.display = 'none';
      return;
    }

    // 言語タイプを取得（デフォルトはhtml）
    const language = element.getAttribute('data-language') || 'html';
    
    // HTMLをエスケープしてクリーンアップ
    const cleanHTML = this.cleanAndEscapeHTML(originalHTML);
    
    // Prism.js用のcode要素を作成
    const pre = document.createElement('pre');
    const code = document.createElement('code');
    
    // 行番号を表示する場合のクラス追加
    if (this.options.showLineNumbers) {
      pre.classList.add('line-numbers');
    }
    
    // 言語クラスを追加
    code.className = `language-${language}`;
    code.textContent = cleanHTML;
    
    pre.appendChild(code);
    
    // 元の内容を置き換え
    element.innerHTML = '';
    element.appendChild(pre);
    element.classList.add('processed', this.options.className);
    
    // Prism.jsでハイライト
    if (window.Prism) {
      window.Prism.highlightElement(code);
    }
  }

  disableJsClasses(element) {
    // 内部のjs-*クラスを持つ要素を取得
    const jsElements = element.querySelectorAll('[class*="js-"]');
    
    jsElements.forEach(jsElement => {
      const classList = Array.from(jsElement.classList);
      const jsClasses = classList.filter(className => className.startsWith('js-'));
      
      // js-*クラスをdata-disabled-js属性に移動
      if (jsClasses.length > 0) {
        jsElement.setAttribute('data-disabled-js', jsClasses.join(' '));
        jsClasses.forEach(jsClass => {
          jsElement.classList.remove(jsClass);
        });
      }
    });
    
    // さらにdata-*属性も無効化（js機能を完全に無効化）
    const dataElements = element.querySelectorAll('[data-target], [data-elm], [data-filter], [data-items-per-page]');
    dataElements.forEach(dataElement => {
      // data属性をdata-disabled-*に変更
      const attributes = Array.from(dataElement.attributes);
      attributes.forEach(attr => {
        if (attr.name.startsWith('data-') && !attr.name.startsWith('data-disabled-') && !attr.name.startsWith('data-language')) {
          dataElement.setAttribute(`data-disabled-${attr.name}`, attr.value);
          dataElement.removeAttribute(attr.name);
        }
      });
    });
  }

  removeDynamicElements(element) {
    // CreateInnerLinksで生成されたulリストを削除（元のコンテンツのulは保持）
    const generatedUls = element.querySelectorAll('ul');
    generatedUls.forEach(ul => {
      // CreateInnerLinksが生成したulのみ削除（通常はjs-create-innerlinksの親要素内にある）
      const isCreateInnerLinksGenerated = ul.closest('.js-create-innerlinks') || 
                                         ul.classList.contains('p-innerlinks-list') ||
                                         ul.querySelector('.js-innerlink');
      
      if (isCreateInnerLinksGenerated && !ul.hasAttribute('data-original')) {
        ul.remove();
      }
    });
    
    // その他の動的生成要素があれば追加で削除
    // 例: スライダーのコントロール要素など
    const splideControls = element.querySelectorAll('.splide__arrows, .splide__pagination');
    splideControls.forEach(control => control.remove());
  }

  cleanAndEscapeHTML(html) {
    // 行を分割して処理
    const lines = html.split('\n');
    
    // 前後の空行を削除
    while (lines.length > 0 && lines[0].trim() === '') {
      lines.shift();
    }
    while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
      lines.pop();
    }
    
    if (lines.length === 0) return '';
    
    // 非空行の最小インデントを計算
    const nonEmptyLines = lines.filter(line => line.trim() !== '');
    if (nonEmptyLines.length === 0) return '';
    
    // インデントを決定: 最初の行が0の場合は、2行目以降の最小インデントを使用
    const firstLineIndent = nonEmptyLines[0].match(/^(\s*)/)[1].length;
    let baseIndent = firstLineIndent;
    
    if (firstLineIndent === 0 && nonEmptyLines.length > 1) {
      // 最初の行が0インデントの場合、2行目以降の最小インデントを基準にする
      const remainingLines = nonEmptyLines.slice(1);
      baseIndent = Math.min(...remainingLines.map(line => {
        const match = line.match(/^(\s*)/);
        return match ? match[1].length : 0;
      }));
    }
    
    // ベースインデントを削除
    const adjustedLines = lines.map(line => {
      if (line.trim() === '') return '';
      const currentIndent = line.match(/^(\s*)/)[1].length;
      // ベースインデント以上の場合は削除
      if (currentIndent >= baseIndent) {
        return line.substring(baseIndent);
      }
      return line;
    });
    
    return adjustedLines.join('\n');
  }


  // 動的に追加された要素を処理
  refresh() {
    this.init();
  }

  // 特定の要素のみ処理
  processNewElement(element) {
    if (element && element.matches(this.options.selector)) {
      this.processElement(element);
    }
  }
}